import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, CpfField, CurrencyField, DataTable, EmptyState, FilterBar, FilterChips, FiltroPill,
  FormGrid, IconButton, MaisFiltros, MaisFiltrosGrupo, NumberField, Pagination, Pill, SelectField,
  TelefoneField, TextField, type Column, type MaisFiltrosResumo, fmtBRL, fmtDataISO,
} from "@atlas/ui/web";
import { motivoVazio, POR_PAGINA_PADRAO, recorteDoServidor } from "../../lib/recorte";
import { useDebouncedValue } from "../../lib/use-debounce";
import { useFiltros } from "../../lib/use-filtros";
import { atlas } from "../../lib/sdk";
import { useUnidadeAtiva } from "../../lib/unidade-ativa";
import { mensagemDeErro } from "../../lib/erro-validacao.js";
import type { PrefeituraServidor, RegraMargemDaTela, ServidorCampoConfig } from "@atlas/sdk";
import { pctDaRegra, rotuloDaColunaSalario } from "../../lib/regra-margem-tela";
import { PageHeader, inp } from "./_ui";
import { ServidorDetalheModal } from "../../components/ServidorDetalheModal";

// VINCULOS constante removida — modal dinamico usa VINCULO_OPTS ({value,label}).
const fmtCpf = (cpf: string) => {
  const d = (cpf ?? "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : (cpf || "—");
};
const fmtTel = (tel?: string) => {
  const d = (tel ?? "").replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d || "—";
};

// Busca via matchAny (lib/text-search) — fonte unica compartilhada com todas
// as outras telas do sistema.

/** Extrai valor por key (built-in ou custom). Custom fields vem em camposCustom. */
function getValor(s: PrefeituraServidor, key: string): string | number | null | undefined {
  if (key === "cpf") return s.cpf;
  const custom = (s as unknown as { camposCustom?: Record<string, string | number | null> }).camposCustom;
  if (key.startsWith("custom_") && custom) return custom[key];
  return (s as unknown as Record<string, string | number | null | undefined>)[key];
}

/** Traduz um campo da config (averbadora) em Column<PrefeituraServidor>. Mesma
 *  regra visual da /averbadora/servidores/visualizar — CPF mascarado, telefone
 *  BR, vazio -> "—", situacao vira Pill, moeda em BRL. */
function campoParaColuna(
  campo: { key: string; label: string; tipo: ServidorCampoConfig["tipo"] },
  convenioNomePorId: Map<string, string>,
): Column<PrefeituraServidor> {
  const base: Column<PrefeituraServidor> = {
    key: campo.key,
    header: campo.label,
    // mono desligado (cliente 24/07/2026): tabela usa fonte unica em todas
    // as colunas. Alinhado com averbadora/servidores/visualizar.
  };
  base.render = (s) => {
    const v = getValor(s, campo.key);
    const dim = <span style={{ color: "var(--text-dim)" }}>—</span>;
    if (v == null || v === "") return dim;
    if (campo.tipo === "numero" && Number(v) === 0) return dim;
    if (campo.tipo === "moeda" && Number(v) === 0) return dim;
    if (campo.key === "cpf") return <>{fmtCpf(String(v))}</>;
    if (campo.key === "telefone" || campo.tipo === "telefone") return <>{fmtTel(String(v))}</>;
    if (campo.key === "situacaoFuncional") {
      const situ = String(v).trim().toUpperCase();
      if (!situ) return <span style={{ color: "var(--text-dim)", fontSize: 12 }}>NÃO INFORMADO</span>;
      const variant: "expirado" | "averbado" | "pendente" =
        /desligado|aposentad/i.test(situ) ? "expirado"
        : /afastad|licenc|ferias/i.test(situ) ? "pendente"
        : "averbado";
      return <Pill variant={variant}>{situ}</Pill>;
    }
    switch (campo.tipo) {
      case "moeda": return <>{fmtBRL(typeof v === "number" ? v : Number(v) || 0)}</>;
      case "numero": return <>{String(v)}</>;
      // Data ISO (YYYY-MM-DD) -> DD/MM/YYYY. Cobre dataAdmissao/dataNascimento
      // e qualquer custom com tipo="data". Cliente 28/07/2026.
      case "data": return <>{fmtDataISO(String(v))}</>;
      default: return <>{String(v)}</>;
    }
  };
  if (campo.tipo === "moeda" || campo.tipo === "numero") base.align = "right";
  return base;
}

export function PrefeituraServidores() {
  // A organizacao sobre a qual esta tela opera — a do login, ou a atendida
  // escolhida no seletor do cabecalho.
  const { chaveDaUnidade, opcoesDoSdk } = useUnidadeAtiva();
  const qc = useQueryClient();
  // Recorte no SERVIDOR: busca, filtros, ordenacao e total saem todos do mesmo
  // recorte na rota. Foi o que a paridade da busca destravou — enquanto o `q` da
  // rota era mais fraco que o desta tela (sem acento, sem multi-termo, sem CPF
  // cru), paginar no servidor faria a busca procurar so dentro da pagina: o
  // operador digita um nome que existe e a tela diz que nao existe. A semantica
  // agora e' UMA, em @atlas/types/busca.
  const [search, setSearch] = useState("");
  // O input mostra `search` (a digitacao tem que aparecer na hora); a consulta
  // usa o atrasado. Sem isso, cada tecla e' uma queryKey nova, ou seja um
  // request — cinco pra digitar "maria", quatro deles jogados fora.
  const buscaDebounced = useDebouncedValue(search);
  const [vinculoFiltro, setVinculoFiltro] = useState("");
  const [situacaoFiltro, setSituacaoFiltro] = useState("");
  const [cargoFiltro, setCargoFiltro] = useState("");
  // Faixa de margem DISPONIVEL, em texto: o input e' numerico mas o estado
  // guarda string porque "" e "0" sao coisas diferentes aqui — `0` e' um limite
  // valido, e um `Number("")` viraria 0 e ligaria o filtro sozinho.
  const [margemMin, setMargemMin] = useState("");
  const [margemMax, setMargemMax] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const [editing, setEditing] = useState<PrefeituraServidor | null>(null);
  const [vendo, setVendo] = useState<PrefeituraServidor | null>(null);

  const numeroOuNada = (v: string) => {
    if (v.trim() === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const q = useQuery({
    // A chave carrega a unidade ativa (009) E o recorte do servidor (006): sem a
    // primeira, trocar de unidade reaproveitaria o cache da anterior; sem o
    // segundo, virar a pagina nao refaz a consulta.
    queryKey: ["prefeitura", "servidores", ...chaveDaUnidade(), buscaDebounced, vinculoFiltro, situacaoFiltro, cargoFiltro, margemMin, margemMax, pagina, porPagina],
    queryFn: () => atlas.prefeitura.servidores({
      q: buscaDebounced || undefined,
      vinculo: vinculoFiltro || undefined,
      situacao: situacaoFiltro || undefined,
      cargo: cargoFiltro || undefined,
      margem_min: numeroOuNada(margemMin),
      margem_max: numeroOuNada(margemMax),
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }, opcoesDoSdk()),
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });
  const facetas = q.data?.facetas ?? { vinculos: [], situacoes: [], cargos: [] };

  const filtros = useFiltros(
    [
      { nome: "busca", ligado: search !== "", limpar: () => setSearch("") },
      { nome: "situacao", ligado: situacaoFiltro !== "", limpar: () => setSituacaoFiltro("") },
      { nome: "vinculo", ligado: vinculoFiltro !== "", limpar: () => setVinculoFiltro("") },
      { nome: "cargo", ligado: cargoFiltro !== "", limpar: () => setCargoFiltro("") },
      // A faixa e' UMA dimensao, nao duas: o operador pensa "de 500 a 2000".
      { nome: "margem", ligado: margemMin !== "" || margemMax !== "", limpar: () => { setMargemMin(""); setMargemMax(""); } },
    ],
    setPagina,
  );
  const { filtrar } = filtros;

  /** O que esta ligado DENTRO do painel, para o recorte nao ficar invisivel. */
  const resumoAvancado: MaisFiltrosResumo[] = [
    ...(cargoFiltro ? [{ rotulo: "Cargo", valor: cargoFiltro, limpar: () => filtrar(setCargoFiltro)("") }] : []),
    ...(margemMin !== "" || margemMax !== ""
      ? [{
          rotulo: "Margem",
          valor: `${margemMin === "" ? "0" : fmtBRL(Number(margemMin))} – ${margemMax === "" ? "sem teto" : fmtBRL(Number(margemMax))}`,
          limpar: () => { setMargemMin(""); setMargemMax(""); setPagina(1); },
        }]
      : []),
  ];
  // Lookup nome do convenio pelo ID (pra tabela renderizar legivel em vez de
  // "CONV-001"). staleTime alto: convenio muda raramente. Cliente 28/07/2026.
  const conveniosQ = useQuery({
    queryKey: ["prefeitura", "convenios", ...chaveDaUnidade()],
    queryFn: () => atlas.prefeitura.convenios(opcoesDoSdk()),
    staleTime: 5 * 60_000,
  });
  const convenioNomePorId = useMemo(() => {
    const m = new Map<string, string>();
    for (const cv of conveniosQ.data?.convenios ?? []) m.set(cv.id, cv.nome);
    return m;
  }, [conveniosQ.data?.convenios]);
  // Margem e contratos do servidor aberto no 👁. Só o que a LINHA não tem — o
  // cadastro já veio inteiro na listagem. A margem vem da rota (e não da linha,
  // que já a traz) para a ficha ser a MESMA nos dois perfis.
  const detalheQ = useQuery({
    queryKey: ["prefeitura", "servidor-detalhe", ...chaveDaUnidade(), vendo?.matricula],
    queryFn: () => atlas.prefeitura.servidorDetalhe(vendo!.matricula, opcoesDoSdk()),
    enabled: !!vendo,
  });
  // AQUI MORAVA O APOIO AO CSV DA BASE DE SERVIDORES, e ele saiu em 07/09/2026
  // (item 4 do PENDENCIAS.md). Eram uma query `me` extra, um `unidadeIdSeguro`
  // e um `columnsHint` montado com destaque para os campos personalizados —
  // nada disso era renderizado.
  //
  // Nao foi "esqueceram de passar as props": o destino nao existia em nenhuma
  // camada. Nao ha link "Baixar exemplo" nesta tela, e as tres URLs que o SDK
  // construia (`servidoresCsvTemplateUrl`, `servidoresLayoutTecnicoUrl` e a
  // variante `.md`) apontavam para rotas que a API NUNCA teve — a camada
  // prefeitura serve `campos-config`, a listagem, o `PATCH /:matricula` e
  // `/exportar`, e nada mais. Os tres construtores sairam junto.
  //
  // O `unidadeIdSeguro` ainda tinha um defeito proprio, herdado da 009: o
  // segundo fallback era o id da organizacao DO LOGIN, e com o seletor de
  // unidades o operador pode estar operando outra. Quem responde isso hoje e
  // `useUnidadeAtiva().unidadeId`, com o nome ja generico.
  //
  // Se um dia a importacao por CSV ganhar apoio na tela, isto volta como
  // feature com spec — as rotas primeiro, e resolvendo antes que "layout
  // tecnico" hoje significa DUAS coisas no portal: o dicionario da base de
  // servidores e o layout de remessa/retorno da unidade (ADR-0007).
  //
  // Config de campos que a averbadora definiu pra esta unidade — e o que dita
  // a ordem e os rotulos das colunas da tabela.
  //  - staleTime alto: config muda raramente (averbadora edita "as vezes")
  //  - retry 1: se falhar por 1s de blip, tenta 1x; senao cai no fallback
  //  - refetchOnWindowFocus false: nao repete request cada vez que a aba volta
  const camposQ = useQuery({
    queryKey: ["prefeitura", "servidores-campos", ...chaveDaUnidade()],
    queryFn: () => atlas.prefeitura.servidorCamposConfig(opcoesDoSdk()),
    // staleTime curto: se a averbadora acabou de adicionar um custom, o user
    // ve o novo campo assim que abre a tela (nao precisa esperar 5min).
    staleTime: 10_000,
    retry: 1,
    // Refetch ao voltar pra tela — captura mudancas feitas pela averbadora em
    // outra aba/sessao.
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
  const camposVisiveis = (camposQ.data?.campos ?? [])
    .filter((c) => c.visivel)
    .sort((a, b) => a.ordem - b.ordem);
  // A pagina JA vem fatiada e o total vem ao lado. `recorteDoServidor` da a
  // mesma forma de saida que o recorte no cliente, e e' o que deixa a diferenca
  // invisivel na tela: o `Pagination` nao sabe (nem precisa saber) de onde veio.
  //
  // Busca e filtros ficam na ROTA — ela busca em nome/matricula/cpf/cpfMasked
  // com a mesma semantica de antes (@atlas/types/busca), e a ordem da tabela
  // tambem vem de lá.
  const daPagina = recorteDoServidor(q.data?.servidores ?? [], q.data?.total ?? 0, pagina, porPagina);

  // Columns dinamicas: ordem + rotulos vem da config que a averbadora definiu
  // em /averbadora/servidores/importar. Assim as duas telas (averbadora ver
  // servidores + prefeitura servidores) ficam SEMPRE na mesma ordem pros
  // mesmos campos. "Margem disp." (calc backend, nao vem da config) e
  // "Acoes" (editar) ficam SEMPRE ao final. Fallback: deriva de
  // CAMPOS_FALLBACK (fonte unica no top-level do modulo) pra tela nao
  // divergir do backend nem duplicar manutencao. Cliente reportou
  // 24/07/2026 divergencia (nome-first vs cpf-first).
  const CAMPOS_FALLBACK_ORDEM: { key: string; label: string; tipo: ServidorCampoConfig["tipo"] }[] =
    CAMPOS_FALLBACK.map((c) => ({ key: c.key, label: c.label, tipo: c.tipo }));
  // Com `margem_da_folha`, a coluna do salario traz a margem de emprestimo que
  // a folha mandou (Caucaia): o rotulo diz isso, em vez de "Salario liquido".
  const regraMargem = q.data?.regraMargem;
  const camposParaTabela: { key: string; label: string; tipo: ServidorCampoConfig["tipo"] }[] = (
    camposVisiveis.length > 0
      ? camposVisiveis
          .filter((c) => c.key !== "acoes")
          .map((c) => ({ key: c.key, label: c.label, tipo: c.tipo }))
      : CAMPOS_FALLBACK_ORDEM
  ).map((c) => (c.key === "salarioLiquido" ? { ...c, label: rotuloDaColunaSalario(regraMargem, c.label) } : c));

  const columns: Column<PrefeituraServidor>[] = [
    ...camposParaTabela.map((c) => campoParaColuna(c, convenioNomePorId)),
    { key: "margemDisponivel", header: "Margem disp.", align: "right", render: (s) => (
      <MargemCell
        salario={s.salarioLiquido}
        margemTotal={s.margemTotal ?? 0}
        margemDisponivel={s.margemDisponivel ?? 0}
        regra={regraMargem}
      />
    ) },
    {
      key: "acoes",
      header: "",
      align: "right",
      // 👁 antes do ✎ — consultar vem antes de alterar, mesma ordem e mesmos
      // ícones de bancos e de órgãos e entidades. Antes só havia "✎ Editar", e
      // conferir um servidor obrigava a abrir o formulário de edição.
      render: (s) => (
        <div style={{ display: "inline-flex", gap: 4 }}>
          <IconButton title={`Visualizar ${s.nome}`} onClick={() => setVendo(s)}>👁</IconButton>
          <IconButton title={`Editar ${s.nome}`} onClick={() => setEditing(s)}>✎</IconButton>
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <PageHeader
        title="Servidores do município"
        subtitle="A prefeitura é a fonte da base. Campos críticos (cargo, endereço, matrícula) só a prefeitura edita."
      />


      <FilterBar
        layout="linha"
        searchValue={search}
        onSearchChange={filtrar(setSearch)}
        searchPlaceholder="Nome, matrícula ou CPF..."
        {...filtros.propsFilterBar}
        actions={
          /*
            Os valores saem das FACETAS da rota, nao de uma lista fixa: `vinculo`
            e `situacaoFuncional` sao strings livres no que a base guarda (o
            import por CSV faz cast, nao validacao), entao uma base cujos
            servidores estao todos como "TRABALHANDO" mostraria 0 em "ATIVO".

            COM CONTAGEM desde 04/09/2026 — a nota antiga dizia que o numero
            teria de sair da base inteira "que a tela nao tem", e isso deixou de
            ser verdade: a rota agora conta com semantica `exceto` (FR-010) e
            manda junto. Com quatro dimensoes mais a faixa, chip sem numero
            fazia o operador clicar no escuro.
          */
          facetas.situacoes.length > 1 ? (
            <FilterChips
              label="Situação"
              value={situacaoFiltro}
              onChange={filtrar(setSituacaoFiltro)}
              options={[
                { value: "", label: "Todas" },
                ...facetas.situacoes.map((s) => ({ value: s.valor, label: s.valor, count: s.contagem })),
              ]}
            />
          ) : null
        }
      >
        {/* VINCULO: chips ate 5 opcoes, pill acima disso. O limite nao e'
            arbitrario — e' o que o proprio FilterChips documenta como razoavel
            para uma linha de chips soltos, e uma base heterogenea passa disso
            fácil (estatutario, CLT, comissionado, aposentado, pensionista,
            temporario...). A rota aceita UM vinculo; multisselecao exigiria
            contrato novo e nao e' o que a conferencia pede. */}
        {facetas.vinculos.length > 5 ? (
          <FiltroPill
            label="Vínculo"
            opcoes={facetas.vinculos.map((v) => ({ value: v.valor, label: `${v.valor} (${v.contagem})` }))}
            selecionados={vinculoFiltro ? [vinculoFiltro] : []}
            onChange={(vs: string[]) => filtrar(setVinculoFiltro)(vs[vs.length - 1] ?? "")}
            buscaPlaceholder="Buscar vínculo"
          />
        ) : facetas.vinculos.length > 1 ? (
          <FilterChips
            label="Vínculo"
            value={vinculoFiltro}
            onChange={filtrar(setVinculoFiltro)}
            options={[
              { value: "", label: "Todos" },
              ...facetas.vinculos.map((v) => ({ value: v.valor, label: v.valor, count: v.contagem })),
            ]}
          />
        ) : null}

        <MaisFiltros ativos={resumoAvancado.length} resumo={resumoAvancado}>
          <MaisFiltrosGrupo titulo="Cargo">
            {/* Cargo e' texto livre vindo do RH — uma prefeitura media tem
                dezenas. Pill desde a primeira opcao, sem o degrade de chips. */}
            <FiltroPill
              label="Cargo"
              opcoes={facetas.cargos.map((c) => ({ value: c.valor, label: `${c.valor} (${c.contagem})` }))}
              selecionados={cargoFiltro ? [cargoFiltro] : []}
              onChange={(vs: string[]) => filtrar(setCargoFiltro)(vs[vs.length - 1] ?? "")}
              buscaPlaceholder="Buscar cargo"
            />
          </MaisFiltrosGrupo>
          <MaisFiltrosGrupo titulo="Margem disponível">
            {/* Faixa em reais. Custa CPU na rota (margem sai de salario menos
                comprometido, varrendo contratos), e por isso a rota so' calcula
                quando um dos dois campos vem preenchido. */}
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
              De R$
              <input
                type="number" min={0} step={100} inputMode="decimal"
                value={margemMin}
                onChange={(e) => filtrar(setMargemMin)(e.target.value)}
                style={{ ...inp, width: 120 }}
              />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
              até R$
              <input
                type="number" min={0} step={100} inputMode="decimal"
                value={margemMax}
                onChange={(e) => filtrar(setMargemMax)(e.target.value)}
                style={{ ...inp, width: 120 }}
              />
            </label>
          </MaisFiltrosGrupo>
        </MaisFiltros>
      </FilterBar>
      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(s) => s.matricula}
        loading={q.isLoading}
        // Distinguir "nada bate com o filtro" de "nao ha dados" (FR-013): lido
        // como base vazia, o operador vai procurar o erro na carga inicial em
        // vez de limpar a busca.
        //
        // Passou a usar `motivoVazio` em 04/09/2026: a nota anterior dizia que
        // ele "nao serve aqui" porque precisa do total da BASE e a rota so'
        // devolvia o do recorte. A rota agora devolve `totalBase`, entao a
        // decisao sai do dado em vez de sair de `temFiltro` — que erra no caso
        // de base vazia COM filtro ligado (dizia "limpe os filtros" numa
        // prefeitura que ainda nao teve carga).
        emptyState={q.isError
          ? "Não foi possível carregar a base de servidores."
          : <EmptyState
              motivo={motivoVazio(q.data?.totalBase ?? 0, daPagina.total)}
              filtroVazio="Nenhum servidor bate com esses filtros. Limpe os filtros para ver o resto da base."
              semDados="Nenhum servidor na base. A carga inicial é feita pela Atlas no onboarding."
              onLimpar={filtros.limparFiltros}
            />}
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="servidores"
      />

      {editing ? <EditModal servidor={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["prefeitura"] }); }} /> : null}

      {vendo ? (
        <ServidorDetalheModal
          nome={vendo.nome}
          // A MESMA lista que monta as colunas — ficha e tabela nunca discordam
          // de rótulo, ordem ou formato.
          campos={camposParaTabela}
          valores={Object.fromEntries(camposParaTabela.map((c) => [c.key, getValor(vendo, c.key)]))}
          detalhe={detalheQ.data}
          carregando={detalheQ.isLoading}
          erro={detalheQ.isError}
          carregarHistorico={(adf) => atlas.prefeitura.historicoPagamentosAdf(adf, opcoesDoSdk())}
          onClose={() => setVendo(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Celula da coluna "Margem disp." com popover explicando a regra.
 * Hover mostra: de onde vem a margem de emprestimo (salario x percentual da
 * regra do orgao, a base legal, ou a margem que a folha mandou pronta), quanto
 * ja esta comprometido em contratos ativos e quanto sobra. Cliente pediu
 * 23/07/2026 um "recurso de Popovers para informar que esta sendo descontado do
 * salario liquido 35% da margem"; o percentual agora vem da regra vigente.
 */
function MargemCell({ salario, margemTotal, margemDisponivel, regra }: {
  salario: number;
  margemTotal: number;
  margemDisponivel: number;
  regra?: RegraMargemDaTela;
}) {
  const [aberto, setAberto] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  // Posicao FIXA (viewport) calculada a partir do "i" — o popover e' renderizado
  // via portal no <body>, FORA da tabela. A tabela tem overflow-x:auto (scroll
  // horizontal) que, por spec CSS, tambem recorta o eixo Y — entao um popover
  // absolute dentro da celula ficava CORTADO (cliente reportou 24/07/2026, o
  // "abrir pra cima" nao resolvia porque quem corta e' o container, nao a
  // janela). Com portal + position:fixed ele escapa do recorte.
  const [coords, setCoords] = useState<{ top: number; right: number; acima: boolean } | null>(null);
  const comprometido = Math.max(0, margemTotal - margemDisponivel);
  const pctUso = margemTotal > 0 ? Math.round((comprometido / margemTotal) * 100) : 0;
  const abrir = () => {
    const el = wrapRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const acima = window.innerHeight - rect.bottom < 220; // ~200px de altura
      setCoords({
        top: acima ? rect.top - 6 : rect.bottom + 6,
        right: window.innerWidth - rect.right,
        acima,
      });
    }
    setAberto(true);
  };
  return (
    <span
      ref={wrapRef}
      style={{ position: "relative", cursor: "help", display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0, whiteSpace: "nowrap" }}
      onMouseEnter={abrir}
      onMouseLeave={() => setAberto(false)}
      onFocus={abrir}
      onBlur={() => setAberto(false)}
      tabIndex={0}
    >
      <span>{fmtBRL(margemDisponivel)}</span>
      <span aria-label="Como esta margem é calculada" style={{
        fontSize: 10, color: "var(--text-dim)", border: "1px solid var(--border-strong)", borderRadius: "50%",
        width: 14, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
        flexShrink: 0,
      }}>i</span>
      {aberto && coords ? createPortal(
        <span
          role="tooltip"
          style={{
            position: "fixed", right: coords.right,
            ...(coords.acima ? { bottom: window.innerHeight - coords.top } : { top: coords.top }),
            zIndex: 1000, pointerEvents: "none",
            background: "var(--bg-elev)", border: "1px solid var(--border-strong)", borderRadius: 8,
            padding: "10px 12px", boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
            minWidth: 260, fontSize: 12, color: "var(--text)", textAlign: "left",
            fontWeight: 400,
          }}
        >
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-dim)", fontWeight: 700, marginBottom: 6 }}>
            Como calcula
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "4px 12px" }}>
            {regra?.base === "margem_da_folha" ? (
              <>
                <span style={{ color: "var(--text-muted)" }}>Margem informada pela folha</span>
                <b style={{ fontFamily: "var(--font-mono)" }}>{fmtBRL(margemTotal)}</b>
              </>
            ) : regra?.base === "liquida_legal" ? (
              <>
                <span style={{ color: "var(--text-muted)" }}>{pctDaRegra(regra.percentuais.EMPRESTIMO)} da base líquida legal</span>
                <b style={{ fontFamily: "var(--font-mono)" }}>{fmtBRL(margemTotal)}</b>
              </>
            ) : (
              <>
                <span style={{ color: "var(--text-muted)" }}>Salário líquido</span>
                <b style={{ fontFamily: "var(--font-mono)" }}>{fmtBRL(salario)}</b>
                <span style={{ color: "var(--text-muted)" }}>
                  {regra ? `× ${pctDaRegra(regra.percentuais.EMPRESTIMO)} (regra do órgão)` : "× percentual da regra do órgão"}
                </span>
                <b style={{ fontFamily: "var(--font-mono)" }}>{fmtBRL(margemTotal)}</b>
              </>
            )}
            <span style={{ color: "var(--text-muted)" }}>− contratos ativos</span>
            <b style={{ fontFamily: "var(--font-mono)", color: "var(--danger-500)" }}>−{fmtBRL(comprometido)}</b>
            <span style={{ fontWeight: 700, borderTop: "1px solid var(--border-strong)", paddingTop: 4 }}>Disponível</span>
            <b style={{ fontFamily: "var(--font-mono)", color: "var(--emerald-500)", borderTop: "1px solid var(--border-strong)", paddingTop: 4 }}>{fmtBRL(margemDisponivel)}</b>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-dim)" }}>
            Uso atual: <b style={{ color: pctUso >= 90 ? "var(--danger-500)" : pctUso >= 70 ? "var(--gold-500)" : "var(--text)" }}>{pctUso}%</b>. Cartão consignado e cartão benefício têm limites próprios
            {regra ? ` (${pctDaRegra(regra.percentuais.CARTAO_CONSIGNADO)} + ${pctDaRegra(regra.percentuais.CARTAO_BENEFICIOS)}${regra.base === "margem_da_folha" ? ", na proporção da margem da folha" : ""})` : ""}, não descontam desta.
          </div>
        </span>,
        document.body,
      ) : null}
    </span>
  );
}

// Fallback quando o backend nao retornar config (isolate frio, endpoint 404,
// etc). Cobre os mesmos campos SISTEMA que existiam antes da tela dinamica.
// Ordem canonica bate com admin/servidor-campos.ts CHAVES_SISTEMA no backend.
// Fonte unica — CAMPOS_FALLBACK_ORDEM dentro do componente deriva daqui pra
// nao divergir. Auditoria UX 27/07/2026 tier 4.
const CAMPOS_FALLBACK: ServidorCampoConfig[] = [
  { key: "cpf", label: "CPF", tipo: "texto", obrigatorio: true, visivel: true, ordem: 0, sistema: true, travado: true },
  { key: "matricula", label: "Matrícula", tipo: "texto", obrigatorio: true, visivel: true, ordem: 1, sistema: true, travado: true },
  { key: "email", label: "E-mail", tipo: "email", obrigatorio: false, visivel: true, ordem: 2, sistema: true },
  { key: "nome", label: "Nome", tipo: "texto", obrigatorio: true, visivel: true, ordem: 3, sistema: true },
  { key: "telefone", label: "Telefone", tipo: "telefone", obrigatorio: false, visivel: true, ordem: 4, sistema: true },
  { key: "cargo", label: "Cargo", tipo: "texto", obrigatorio: false, visivel: true, ordem: 5, sistema: true },
  { key: "vinculo", label: "Vínculo", tipo: "texto", obrigatorio: false, visivel: true, ordem: 6, sistema: true },
  { key: "situacaoFuncional", label: "Situação funcional", tipo: "texto", obrigatorio: false, visivel: true, ordem: 7, sistema: true },
  { key: "salarioLiquido", label: "Salário líquido", tipo: "moeda", obrigatorio: false, visivel: true, ordem: 8, sistema: true },
  { key: "dataAdmissao", label: "Admissão", tipo: "data", obrigatorio: false, visivel: true, ordem: 10, sistema: true },
  { key: "dataNascimento", label: "Nascimento", tipo: "data", obrigatorio: false, visivel: true, ordem: 11, sistema: true },
  { key: "endereco", label: "Endereço", tipo: "texto", obrigatorio: false, visivel: true, ordem: 12, sistema: true },
];

/** Sistema fields que o backend PATCH aceita persistir. Custom fields
 *  (nao-sistema) tambem sao editaveis — vao no payload camposCustom. */
const CAMPOS_EDITAVEIS = new Set([
  "nome", "cpf", "matricula", "cargo", "endereco", "vinculo", "email",
  "telefone", "salarioLiquido", "situacaoFuncional",
  "dataAdmissao", "dataNascimento",
]);
function ehEditavel(c: ServidorCampoConfig): boolean {
  if (c.travado === true) return false;
  if (CAMPOS_EDITAVEIS.has(c.key)) return true;
  return c.sistema === false; // custom field
}

const backdrop: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "grid", placeItems: "center", zIndex: 100, padding: 24 };
const modalStyle: React.CSSProperties = { background: "var(--surface-solid)", borderRadius: 12, padding: 24, maxWidth: 720, width: "100%", border: "1px solid var(--border-strong)", boxShadow: "var(--shadow-lg)", maxHeight: "90vh", overflowY: "auto" };

const VINCULO_OPTS = [
  { value: "ESTATUTARIO", label: "Estatutário" },
  { value: "CLT", label: "CLT" },
  { value: "COMISSIONADO", label: "Comissionado" },
  { value: "APOSENTADO", label: "Aposentado" },
  { value: "PENSIONISTA", label: "Pensionista" },
];

function EditModal({ servidor, onClose, onSaved }: { servidor: PrefeituraServidor; onClose: () => void; onSaved: () => void }) {
  const cfgQ = useQuery({
    queryKey: ["prefeitura", "servidor-campos-config"],
    queryFn: () => atlas.prefeitura.getServidorCamposConfig(),
    staleTime: 60_000,
  });
  const campos = (cfgQ.data?.config?.campos ?? CAMPOS_FALLBACK)
    .filter((c) => c.visivel)
    .sort((a, b) => a.ordem - b.ordem);

  const [valores, setValores] = useState<Record<string, string | number>>(() => {
    const src = servidor as unknown as Record<string, unknown>;
    const out: Record<string, string | number> = {};
    const keys = ["nome","cpf","matricula","vinculo","situacaoFuncional","salarioLiquido","cargo","endereco","email","telefone","dataAdmissao","dataNascimento"];
    for (const k of keys) {
      const v = src[k];
      if (typeof v === "number" || typeof v === "string") out[k] = v;
      else out[k] = "";
    }
    out.matricula = servidor.matricula;
    return out;
  });
  const setV = (k: string, v: string | number) => setValores((prev) => ({ ...prev, [k]: v }));

  const [erro, setErro] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      for (const c of campos) {
        if (!c.obrigatorio) continue;
        if (!CAMPOS_EDITAVEIS.has(c.key)) continue;
        const v = valores[c.key];
        const vazio = v == null || (typeof v === "string" && !v.trim()) || (c.tipo === "moeda" && Number(v) === 0);
        if (vazio) throw new Error(`Campo "${c.label}" é obrigatório.`);
      }
      const cpfNovo = String(valores.cpf ?? "").trim();
      const matNova = String(valores.matricula ?? "").trim();
      const cpfDigitos = cpfNovo.replace(/\D/g, "");
      if (cpfNovo && cpfDigitos.length !== 11) throw new Error("CPF deve ter 11 dígitos.");
      // Monta o body so com campos que a config marca como visiveis E que
      // o backend aceita persistir. Reduz risco de sobrescrever com vazio
      // um campo que a prefeitura escondeu do formulario dela.
      const patch: Record<string, string | number> = {};
      const put = (k: string, v: string | number | undefined) => {
        if (v === undefined) return;
        if (!campos.some((c) => c.key === k && c.visivel)) return;
        if (!CAMPOS_EDITAVEIS.has(k)) return;
        patch[k] = v;
      };
      put("nome", String(valores.nome ?? ""));
      put("cargo", String(valores.cargo ?? ""));
      put("endereco", String(valores.endereco ?? ""));
      put("vinculo", String(valores.vinculo ?? ""));
      put("email", String(valores.email ?? ""));
      put("telefone", String(valores.telefone ?? ""));
      put("situacaoFuncional", String(valores.situacaoFuncional ?? ""));
      put("dataAdmissao", String(valores.dataAdmissao ?? ""));
      put("dataNascimento", String(valores.dataNascimento ?? ""));
      if (valores.salarioLiquido != null && valores.salarioLiquido !== "") put("salarioLiquido", Number(valores.salarioLiquido));
      if (cpfDigitos !== (servidor.cpf ?? "").replace(/\D/g, "")) patch.cpf = cpfNovo;
      if (matNova !== servidor.matricula) patch.matriculaNova = matNova;
      // Custom fields visiveis viram um objeto camposCustom no payload.
      const custom: Record<string, string> = {};
      for (const c of campos) {
        if (c.sistema || c.travado) continue;
        const v = valores[c.key];
        if (v == null) continue;
        custom[c.key] = String(v);
      }
      const patchFinal: Record<string, string | number | Record<string, string>> = { ...patch };
      if (Object.keys(custom).length > 0) patchFinal.camposCustom = custom;
      return atlas.prefeitura.editarServidor(servidor.matricula, patchFinal);
    },
    onSuccess: () => { setErro(null); onSaved(); },
    // Mostra o MOTIVO, nao so "Dados invalidos". O backend usa essa mensagem
    // generica pra qualquer recusa e poe o campo + motivo em `details`; sem
    // ler isso, o operador fica sem saber o que corrigir. Cliente 11/08/2026,
    // editando o e-mail de um servidor.
    onError: (e: Error) => setErro(mensagemDeErro(e, "Nao foi possivel salvar o servidor")),
  });

  const renderCampo = (c: ServidorCampoConfig) => {
    const val = valores[c.key];
    const readOnly = !ehEditavel(c);
    const hint = c.travado ? "Travado (identidade do servidor)"
      : c.key === "matricula" ? "Alterar remapeia o servidor"
      : !ehEditavel(c) ? "Somente leitura"
      : undefined;

    if (c.key === "cpf") {
      const cpfDigits = String(val ?? "").replace(/\D/g, "");
      const cpfValido = cpfDigits.length === 11 || cpfDigits.length === 0;
      return (
        <CpfField
          label={c.label}
          value={String(val ?? "")}
          onChange={(e) => setV(c.key, e.target.value)}
          required={c.obrigatorio}
          hint={hint}
          error={!cpfValido ? "CPF deve ter 11 dígitos" : undefined}
          readOnly={readOnly}
        />
      );
    }
    if (c.key === "telefone" || c.tipo === "telefone") {
      return (
        <TelefoneField
          label={c.label}
          value={String(val ?? "")}
          onChange={(e) => setV(c.key, e.target.value)}
          required={c.obrigatorio}
          hint={hint}
          readOnly={readOnly}
        />
      );
    }
    if (c.key === "vinculo") {
      const cur = String(val ?? "ESTATUTARIO");
      const opts = VINCULO_OPTS.slice();
      if (!opts.some((o) => o.value === cur) && cur) opts.unshift({ value: cur, label: cur });
      return (
        <SelectField
          label={c.label}
          value={cur}
          onChange={(e) => setV(c.key, e.target.value)}
          options={opts}
          required={c.obrigatorio}
          hint={hint}
          disabled={readOnly}
        />
      );
    }
    if (c.tipo === "moeda") {
      return (
        <CurrencyField
          label={c.label}
          value={typeof val === "number" ? val : Number(val) || null}
          onValueChange={(n) => setV(c.key, n ?? 0)}
          required={c.obrigatorio}
          hint={hint}
          readOnly={readOnly}
        />
      );
    }
    if (c.tipo === "numero") {
      return (
        <NumberField
          label={c.label}
          value={val ?? ""}
          onChange={(e) => setV(c.key, e.target.value)}
          required={c.obrigatorio}
          hint={hint}
          readOnly={readOnly}
        />
      );
    }
    if (c.tipo === "data") {
      return (
        <TextField
          label={c.label}
          type="date"
          value={String(val ?? "")}
          onChange={(e) => setV(c.key, e.target.value)}
          required={c.obrigatorio}
          hint={hint}
          readOnly={readOnly}
        />
      );
    }
    return (
      <TextField
        label={c.label}
        type={c.tipo === "email" ? "email" : "text"}
        value={String(val ?? "")}
        onChange={(e) => setV(c.key, e.target.value)}
        required={c.obrigatorio}
        hint={hint}
        readOnly={readOnly}
      />
    );
  };

  return (
    <div style={backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Editar servidor</h3>
        <p style={{ color: "var(--text-muted)", fontSize: 12, marginTop: -4 }}>
          Matrícula <b style={{ fontFamily: "var(--font-mono)" }}>{servidor.matricula}</b>
        </p>

        {cfgQ.isPending ? (
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Carregando configuração de campos…</p>
        ) : (
          <FormGrid>
            {campos.map((c) => (
              <span key={c.key}>{renderCampo(c)}</span>
            ))}
          </FormGrid>
        )}

        {erro || save.isError ? (
          <p style={{ color: "var(--danger-500)", fontSize: 13, marginTop: 12 }}>
            {erro ?? (save.error as Error | undefined)?.message}
          </p>
        ) : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || cfgQ.isPending}>
            {save.isPending ? "Salvando…" : "Salvar alterações"}
          </Button>
        </div>
      </div>
    </div>
  );
}
